import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import Card from "components/card/Card";
import Checkbox from "components/form/checkbox/Checkbox";
import { valueToString } from "helpers/Helpers";
import { IPermissionDetails, IPermissionDetailsList } from "api/rpc/2024-04/facilityAdmin/client/admin/admin";
import "../staffPermissions.scss";

const BULK_EXCLUDED_KEYS = ["tee_sheet_starter", "kiosk_view"];
const REPORTS_ACCESS_KEY = "reports_access";
const REPORT_GROUP_FADE_MS = 240;

function isIndividualReportPermission(permission: IPermissionDetails) {
  return permission.key.startsWith("reports_facility_") || permission.key.startsWith("reports_client_");
}

function reportsAccessIsOn(
  permissions: IPermissionDetailsList | null,
  isChecked: (permission: IPermissionDetails) => boolean,
) {
  if (!permissions) {
    return true;
  }

  const accessPermission = Object.values(permissions)
    .flatMap(groups => Object.values(groups).flat())
    .find(permission => permission.key === REPORTS_ACCESS_KEY);

  return accessPermission ? isChecked(accessPermission) : true;
}

function groupRevealsReportPermissions(permissionList: IPermissionDetails[]) {
  return (
    permissionList.some(isIndividualReportPermission) &&
    permissionList.every(permission => permission.key !== REPORTS_ACCESS_KEY)
  );
}

interface IPermissionEditorProps {
  permissions: IPermissionDetailsList | null;
  title: string;
  disabled?: boolean;
  isChecked: (permission: IPermissionDetails) => boolean;
  onToggle: (permission: IPermissionDetails, checked: boolean) => void;
  onToggleMany: (permissions: IPermissionDetails[], checked: boolean) => void;
  isRowDisabled?: (permission: IPermissionDetails) => boolean;
  rowDescription?: (permission: IPermissionDetails) => string | undefined;
}

export default function PermissionEditor(props: IPermissionEditorProps) {
  const { permissions, title, disabled = false, isChecked, onToggle, onToggleMany, isRowDisabled, rowDescription } =
    props;
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>();
  const [searchFilter, setSearchFilter] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});
  const categoryNavRef = useRef<HTMLDivElement>(null);
  const permissionsHeaderRef = useRef<HTMLDivElement>(null);
  const pendingScrollCategory = useRef<string | null>(null);
  const didSyncReportAccess = useRef(false);
  const previousReportAccess = useRef(true);
  const reportMotionTimer = useRef<number | null>(null);
  const accessOn = reportsAccessIsOn(permissions, isChecked);
  const [showReportPermissions, setShowReportPermissions] = useState(accessOn);
  const [reportMotion, setReportMotion] = useState<"idle" | "enter" | "exit">("idle");

  function permissionMatchesFilter(permission: IPermissionDetails, filter: string) {
    if (!filter) {
      return true;
    }

    const query = filter.toLowerCase();
    return [permission.key, permission.category, permission.subcategory, permission.title, permission.description].some(
      field => field?.toLowerCase().includes(query),
    );
  }

  function visibleGroupPermissions(permissionList: IPermissionDetails[]) {
    if (showReportPermissions) {
      return permissionList;
    }

    return permissionList.filter(permission => !isIndividualReportPermission(permission));
  }

  function categoryPermissions(categoryKey: string) {
    if (!permissions?.[categoryKey]) {
      return [] as IPermissionDetails[];
    }

    return visibleGroupPermissions(Object.values(permissions[categoryKey]).flat());
  }

  function everyPermission() {
    if (!permissions) {
      return [] as IPermissionDetails[];
    }

    return Object.values(permissions).flatMap(groups => Object.values(groups).flat());
  }

  function categoryHasMatch(categoryKey: string, filter: string) {
    return categoryPermissions(categoryKey).some(permission => permissionMatchesFilter(permission, filter));
  }

  function countEnabled(permissionList: IPermissionDetails[]) {
    return permissionList.filter(permission => isChecked(permission)).length;
  }

  function bulkPermissions(permissionList: IPermissionDetails[]) {
    return permissionList.filter(permission => !BULK_EXCLUDED_KEYS.includes(permission.key));
  }

  function selectionState(permissionList: IPermissionDetails[]) {
    const relevant = bulkPermissions(permissionList);
    const list = relevant.length > 0 ? relevant : permissionList;
    const enabledCount = list.filter(permission => isChecked(permission)).length;

    return {
      checked: list.length > 0 && enabledCount === list.length,
      indeterminate: enabledCount > 0 && enabledCount < list.length,
    };
  }

  function permissionsHeaderOffset() {
    return permissionsHeaderRef.current?.offsetHeight ?? 0;
  }

  function scrollToCategory(categoryKey: string) {
    const container = document.querySelector(".ui-main_container");
    const section = sectionRefs.current[categoryKey];
    if (!container || !section) {
      return;
    }

    const top =
      container.scrollTop +
      (section.getBoundingClientRect().top - container.getBoundingClientRect().top) -
      permissionsHeaderOffset() -
      12;
    container.scrollTo({ top, behavior: "smooth" });
  }

  function handleSearchInput(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    setSearchFilter(value);

    if (!value.trim() || !permissions) {
      return;
    }

    if (selectedCategory && categoryHasMatch(selectedCategory, value)) {
      return;
    }

    const nextCategory = Object.keys(permissions)
      .sort((a, b) => a.localeCompare(b))
      .find(categoryKey => categoryHasMatch(categoryKey, value));

    if (nextCategory) {
      pendingScrollCategory.current = nextCategory;
      setSelectedCategory(nextCategory);
    }
  }

  function handleRowKeyDown(event: React.KeyboardEvent<HTMLDivElement>, rowDisabled: boolean, onActivate: () => void) {
    if ((event.target as HTMLElement).closest(".staff-permissions-group-toggle")) {
      return;
    }

    if (rowDisabled || (event.key !== " " && event.key !== "Enter")) {
      return;
    }

    event.preventDefault();
    onActivate();
  }

  function groupCollapseKey(categoryKey: string, title: string) {
    return `${categoryKey}::${title}`;
  }

  function isGroupCollapsed(categoryKey: string, title: string) {
    if (searchFilter.trim()) {
      return false;
    }

    return Boolean(collapsedGroups[groupCollapseKey(categoryKey, title)]);
  }

  function toggleGroupCollapsed(categoryKey: string, title: string) {
    const key = groupCollapseKey(categoryKey, title);
    setCollapsedGroups(current => ({ ...current, [key]: !current[key] }));
  }

  const categoryKeys = permissions
    ? Object.keys(permissions)
        .filter(categoryKey => categoryPermissions(categoryKey).length > 0)
        .sort((a, b) => a.localeCompare(b))
    : [];
  const visibleCategoryKeys = searchFilter
    ? categoryKeys.filter(categoryKey => categoryHasMatch(categoryKey, searchFilter))
    : categoryKeys;
  const activeCategory =
    selectedCategory && visibleCategoryKeys.includes(selectedCategory) ? selectedCategory : visibleCategoryKeys[0];
  const visibleCategoryKey = visibleCategoryKeys.join("|");
  const allPermissions = categoryKeys.flatMap(categoryKey => categoryPermissions(categoryKey));
  const totalEnabled = countEnabled(allPermissions);
  const totalCount = allPermissions.length;
  const progress = totalCount === 0 ? 0 : (totalEnabled / totalCount) * 100;
  // With a search, select-all applies only to the permissions still on screen.
  // Otherwise include report permissions that are hidden until Access Reports is turned on.
  const permissionsForSelectAll = searchFilter
    ? allPermissions.filter(permission => permissionMatchesFilter(permission, searchFilter))
    : everyPermission();
  const allSelection = selectionState(permissionsForSelectAll);
  const allBulkPermissions = bulkPermissions(permissionsForSelectAll);

  useLayoutEffect(() => {
    if (!permissions) {
      return;
    }

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!didSyncReportAccess.current) {
      didSyncReportAccess.current = true;
      previousReportAccess.current = accessOn;
      setShowReportPermissions(accessOn);
      return;
    }

    if (previousReportAccess.current === accessOn) {
      return;
    }

    previousReportAccess.current = accessOn;
    if (reportMotionTimer.current !== null) {
      window.clearTimeout(reportMotionTimer.current);
      reportMotionTimer.current = null;
    }

    if (reduceMotion) {
      setShowReportPermissions(accessOn);
      setReportMotion("idle");
      return;
    }

    const motionDuration = REPORT_GROUP_FADE_MS;

    if (accessOn) {
      setShowReportPermissions(true);
      setReportMotion("enter");
      reportMotionTimer.current = window.setTimeout(() => setReportMotion("idle"), motionDuration);
      return;
    }

    setReportMotion("exit");
    reportMotionTimer.current = window.setTimeout(() => {
      setShowReportPermissions(false);
      setReportMotion("idle");
      reportMotionTimer.current = null;
    }, motionDuration);
  }, [accessOn, permissions]);

  useEffect(() => {
    return () => {
      if (reportMotionTimer.current !== null) {
        window.clearTimeout(reportMotionTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!selectedCategory && categoryKeys.length > 0) {
      setSelectedCategory(categoryKeys[0]);
    }
  }, [selectedCategory, visibleCategoryKey]);

  useEffect(() => {
    if (visibleCategoryKeys.length === 0) {
      return;
    }

    const container = document.querySelector(".ui-main_container");
    if (!container) {
      return;
    }

    const updateActiveCategory = () => {
      const canScroll = container.scrollHeight > container.clientHeight + 8;
      const nearBottom = canScroll && container.scrollTop + container.clientHeight >= container.scrollHeight - 8;
      const lastKey = visibleCategoryKeys[visibleCategoryKeys.length - 1];
      if (nearBottom) {
        setSelectedCategory(prev => (prev === lastKey ? prev : lastKey));
        return;
      }

      const marker = container.getBoundingClientRect().top + permissionsHeaderOffset() + 24;
      let current = visibleCategoryKeys[0];
      visibleCategoryKeys.forEach(categoryKey => {
        const section = sectionRefs.current[categoryKey];
        if (section && section.getBoundingClientRect().top <= marker) {
          current = categoryKey;
        }
      });
      setSelectedCategory(prev => (prev === current ? prev : current));
    };

    updateActiveCategory();
    container.addEventListener("scroll", updateActiveCategory, { passive: true });
    return () => container.removeEventListener("scroll", updateActiveCategory);
  }, [visibleCategoryKey]);

  useEffect(() => {
    const categoryKey = pendingScrollCategory.current;
    if (!categoryKey) {
      return;
    }

    pendingScrollCategory.current = null;
    requestAnimationFrame(() => scrollToCategory(categoryKey));
  }, [searchFilter, visibleCategoryKey]);

  useEffect(() => {
    const header = permissionsHeaderRef.current;
    if (!header) {
      return;
    }

    const applyHeaderHeight = () => {
      header.parentElement?.style.setProperty("--staff-permissions-header-height", `${header.offsetHeight}px`);
    };

    applyHeaderHeight();
    const observer = new ResizeObserver(applyHeaderHeight);
    observer.observe(header);
    return () => observer.disconnect();
  }, [permissions]);

  useEffect(() => {
    const nav = categoryNavRef.current;
    if (!nav || !activeCategory) {
      return;
    }

    const button = nav.querySelector<HTMLButtonElement>(`[data-category-nav="${CSS.escape(activeCategory)}"]`);
    if (!button) {
      return;
    }

    const navRect = nav.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();
    if (buttonRect.top < navRect.top) {
      nav.scrollTop -= navRect.top - buttonRect.top + 8;
    } else if (buttonRect.bottom > navRect.bottom) {
      nav.scrollTop += buttonRect.bottom - navRect.bottom + 8;
    }
  }, [activeCategory]);

  return (
    <Card>
      <div className="ui-card-header staff-permissions-header" ref={permissionsHeaderRef}>
        <div>
          <h3 className="text-lg text-semibold">{title}</h3>
          <div className="staff-permissions-summary">
            <div className="staff-permissions-progress" aria-hidden="true">
              <div className="staff-permissions-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span>{t("{{enabled}} of {{total}} enabled", { enabled: totalEnabled, total: totalCount })}</span>
          </div>
        </div>
        <div className="staff-permissions-header-actions">
          <label className="staff-permissions-search">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="text"
              placeholder={t("Search all permissions")}
              value={searchFilter}
              onChange={handleSearchInput}
            />
          </label>
          <div
            className={`staff-permissions-select-all ${disabled ? "is-disabled" : ""}`}
            role="checkbox"
            aria-checked={allSelection.indeterminate ? "mixed" : allSelection.checked}
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onClick={() => {
              if (!disabled) {
                onToggleMany(allBulkPermissions, !allSelection.checked);
              }
            }}
            onKeyDown={event =>
              handleRowKeyDown(event, disabled, () => onToggleMany(allBulkPermissions, !allSelection.checked))
            }
          >
            <Checkbox
              id="select_all_permissions"
              label={t("Select all")}
              checked={allSelection.checked}
              indeterminate={allSelection.indeterminate}
              disabled={disabled}
              readOnly
              tabIndex={-1}
              size="medium"
            />
          </div>
        </div>
      </div>
      <div className="staff-permissions-body">
        <div className="staff-permissions-categories">
          <div className="staff-permissions-categories-nav" ref={categoryNavRef}>
            {visibleCategoryKeys.map(categoryKey => {
              const categoryPermissionList = categoryPermissions(categoryKey);
              const enabled = countEnabled(categoryPermissionList);

              return (
                <button
                  key={categoryKey}
                  type="button"
                  data-category-nav={categoryKey}
                  className={`staff-permissions-category ${activeCategory === categoryKey ? "active" : ""}`}
                  onClick={() => {
                    setSelectedCategory(categoryKey);
                    scrollToCategory(categoryKey);
                  }}
                >
                  <span>{t(valueToString(categoryKey))}</span>
                  <span className="staff-permissions-count">
                    {enabled}/{categoryPermissionList.length}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="staff-permissions-content">
          {permissions && visibleCategoryKeys.length > 0 ? (
            visibleCategoryKeys.map(categoryKey => {
              const sectionPermissions = categoryPermissions(categoryKey);

              return (
                <section
                  key={categoryKey}
                  className="staff-permissions-section"
                  data-category={categoryKey}
                  ref={node => {
                    sectionRefs.current[categoryKey] = node;
                  }}
                >
                  <div className="staff-permissions-section-header">
                    <div>
                      <h4 className="staff-permissions-section-title">{t(valueToString(categoryKey))}</h4>
                      <p className="staff-permissions-section-subtitle">
                        {t("{{enabled}} of {{total}} enabled in this section", {
                          enabled: countEnabled(sectionPermissions),
                          total: sectionPermissions.length,
                        })}
                      </p>
                    </div>
                  </div>
                  <div className="staff-permissions-groups">
                    {(() => {
                      const groupEntries = Object.entries(permissions[categoryKey]);
                      const parentEntry = groupEntries.find(([, groupPermissions]) =>
                        groupPermissions.some(permission => permission.key === REPORTS_ACCESS_KEY),
                      );
                      const nestedEntries = parentEntry
                        ? groupEntries.filter(([, groupPermissions]) => groupRevealsReportPermissions(groupPermissions))
                        : [];
                      const standaloneEntries = groupEntries.filter(([title]) => {
                        if (parentEntry && title === parentEntry[0]) {
                          return false;
                        }

                        return !nestedEntries.some(([nestedTitle]) => nestedTitle === title);
                      });

                      const renderRows = (permissionList: IPermissionDetails[]) =>
                        permissionList.map(permission => {
                          const checked = Boolean(isChecked(permission));
                          const rowDisabled = disabled || Boolean(isRowDisabled?.(permission));
                          const description = rowDescription?.(permission) ?? permission.description;

                          return (
                            <div
                              key={permission.id}
                              className={`staff-permissions-row ${rowDisabled ? "is-disabled" : ""}`}
                              role="checkbox"
                              aria-checked={checked}
                              aria-disabled={rowDisabled || undefined}
                              tabIndex={rowDisabled ? -1 : 0}
                              onClick={() => {
                                if (!rowDisabled) {
                                  onToggle(permission, !checked);
                                }
                              }}
                              onKeyDown={event =>
                                handleRowKeyDown(event, rowDisabled, () => onToggle(permission, !checked))
                              }
                            >
                              <div className="staff-permissions-check">
                                <Checkbox
                                  id={permission.key}
                                  checked={checked}
                                  disabled={rowDisabled}
                                  readOnly
                                  tabIndex={-1}
                                  size="medium"
                                />
                              </div>
                              <div className="staff-permissions-row-copy">
                                <div className="staff-permissions-row-title">{t(permission.title)}</div>
                                {description ? (
                                  <div className="staff-permissions-row-description">{description}</div>
                                ) : null}
                              </div>
                            </div>
                          );
                        });

                      const renderGroupHeader = (
                        title: string,
                        headerClassName: string,
                        visiblePermissions: IPermissionDetails[],
                        availablePermissions: IPermissionDetails[],
                        collapsible = false,
                      ) => {
                        const groupSelection = selectionState(visiblePermissions);
                        const collapsed = collapsible && isGroupCollapsed(categoryKey, title);
                        const toggleVisibleGroup = () => {
                          onToggleMany(bulkPermissions(visiblePermissions), !groupSelection.checked);
                        };

                        return (
                          <div
                            className={`${headerClassName} ${disabled ? "is-disabled" : ""}`}
                            role="checkbox"
                            aria-checked={groupSelection.indeterminate ? "mixed" : groupSelection.checked}
                            aria-disabled={disabled || undefined}
                            tabIndex={disabled ? -1 : 0}
                            onClick={() => {
                              if (!disabled) {
                                toggleVisibleGroup();
                              }
                            }}
                            onKeyDown={event => handleRowKeyDown(event, disabled, toggleVisibleGroup)}
                          >
                            <div className="staff-permissions-check">
                              <Checkbox
                                id={`group_${categoryKey}_${title}`}
                                checked={groupSelection.checked}
                                indeterminate={groupSelection.indeterminate}
                                disabled={disabled}
                                readOnly
                                tabIndex={-1}
                                size="medium"
                              />
                            </div>
                            <div className="staff-permissions-group-title">
                              <span>{t(valueToString(title))}</span>
                              <span className="staff-permissions-group-count">
                                {t("{{enabled}} of {{total}} enabled", {
                                  enabled: countEnabled(availablePermissions),
                                  total: availablePermissions.length,
                                })}
                              </span>
                            </div>
                            {collapsible ? (
                              <button
                                type="button"
                                className={`staff-permissions-group-toggle${collapsed ? " is-collapsed" : ""}`}
                                aria-expanded={!collapsed}
                                aria-label={collapsed ? t("Show permissions") : t("Hide permissions")}
                                onClick={event => {
                                  event.stopPropagation();
                                  toggleGroupCollapsed(categoryKey, title);
                                }}
                                onKeyDown={event => event.stopPropagation()}
                              >
                                <FontAwesomeIcon icon={faChevronDown} />
                              </button>
                            ) : null}
                          </div>
                        );
                      };

                      const renderStandaloneGroup = (title: string, groupPermissions: IPermissionDetails[]) => {
                        const availablePermissions = visibleGroupPermissions(groupPermissions);
                        const visiblePermissions = availablePermissions.filter(permission =>
                          permissionMatchesFilter(permission, searchFilter),
                        );
                        if (visiblePermissions.length === 0) {
                          return null;
                        }

                        return (
                          <div key={title} className="staff-permissions-group">
                            {renderGroupHeader(
                              title,
                              "staff-permissions-group-header",
                              visiblePermissions,
                              availablePermissions,
                            )}
                            {renderRows(visiblePermissions)}
                          </div>
                        );
                      };

                      const nestedGroups = nestedEntries
                        .map(([title, groupPermissions]) => {
                          const visiblePermissions = visibleGroupPermissions(groupPermissions).filter(permission =>
                            permissionMatchesFilter(permission, searchFilter),
                          );
                          if (visiblePermissions.length === 0) {
                            return null;
                          }

                          const nestedCollapsed = isGroupCollapsed(categoryKey, title);

                          return (
                            <div key={title} className="staff-permissions-nested-group">
                              {renderGroupHeader(
                                title,
                                "staff-permissions-nested-header",
                                visiblePermissions,
                                visiblePermissions,
                                true,
                              )}
                              {nestedCollapsed ? null : renderRows(visiblePermissions)}
                            </div>
                          );
                        })
                        .filter(group => group !== null);

                      const parentCard = parentEntry
                        ? (() => {
                            const [parentTitle, parentPermissions] = parentEntry;
                            const parentAvailable = visibleGroupPermissions(parentPermissions);
                            const parentMatching = parentAvailable.filter(permission =>
                              permissionMatchesFilter(permission, searchFilter),
                            );
                            const parentVisible =
                              parentMatching.length > 0 || nestedGroups.length === 0 ? parentMatching : parentAvailable;
                            if (parentVisible.length === 0 && nestedGroups.length === 0) {
                              return null;
                            }

                            const parentCollapsed = isGroupCollapsed(categoryKey, parentTitle);
                            const showNested = nestedGroups.length > 0 && !parentCollapsed;

                            return (
                              <div key={parentTitle} className="staff-permissions-group-stack">
                                <div className="staff-permissions-group">
                                  {parentVisible.length > 0
                                    ? renderGroupHeader(
                                        parentTitle,
                                        "staff-permissions-group-header",
                                        parentVisible,
                                        parentAvailable,
                                        nestedGroups.length > 0,
                                      )
                                    : null}
                                  {parentCollapsed ? null : renderRows(parentVisible)}
                                </div>
                                {showNested ? (
                                  <div
                                    className={`staff-permissions-nested${
                                      reportMotion === "enter"
                                        ? " is-entering"
                                        : reportMotion === "exit"
                                          ? " is-exiting"
                                          : ""
                                    }`}
                                  >
                                    {nestedGroups}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })()
                        : null;

                      return (
                        <>
                          {parentCard}
                          {standaloneEntries.map(([title, groupPermissions]) =>
                            renderStandaloneGroup(title, groupPermissions),
                          )}
                        </>
                      );
                    })()}
                  </div>
                </section>
              );
            })
          ) : (
            <div className="staff-permissions-empty">{t("No permissions match your search.")}</div>
          )}
        </div>
      </div>
    </Card>
  );
}
